import { useQuery } from '@tanstack/react-query';
import { CONSENT_DOCUMENT_KEYS } from '@/constants/legalDocuments';
import { legalService, type LegalDocument } from '@/services/legalService';

export const CONSENT_DOCUMENTS_QUERY_KEY = ['consent-documents'] as const;

/**
 * The documents onboarding asks the user to accept, with their text.
 *
 * Fetched by key rather than through the document list: onboarding already knows which
 * documents it needs, and every user passes through it, so it should not depend on a list
 * request meant for the footer. Never cached — the server records whichever version is
 * published when Accept is pressed, so a stale copy would record agreement to text the
 * user never saw.
 */
export function useConsentDocuments() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: CONSENT_DOCUMENTS_QUERY_KEY,
    queryFn: async () => {
      const results = await Promise.all(
        CONSENT_DOCUMENT_KEYS.map(key => legalService.getDocument(key)),
      );
      return results.filter((entry): entry is LegalDocument => !!entry);
    },
    staleTime: 0,
    refetchOnMount: 'always',
    // A missing document is a definitive answer, not a blip worth retrying.
    retry: false,
  });

  return {
    documents: data ?? [],
    isLoading,
    isError,
    refetch: () => {
      void refetch();
    },
  };
}
